import React, { useEffect, useState } from "react";
import type { TermsContentItem, TermsAudience, TermsItemType, PrivacyPolicyAudience } from "@types";
import {
  fetchTermsContent,
  createTermsContentItem,
  updateTermsContentItem,
  deleteTermsContentItem,
  reorderTermsContent,
} from "../../lib/termsContent";
import {
  fetchPrivacyPolicy,
  upsertPrivacyPolicy,
} from "../../lib/privacyPolicy";

type FormData = {
  item_id: string;
  item_type: TermsItemType;
  body: string;
  label: string;
};

type ContentTab = "terms" | "privacy";

export default function TermsBuilderPage() {
  const [activeTab, setActiveTab] = useState<ContentTab>("terms");
  const [audience, setAudience] = useState<TermsAudience>("seeker");
  const [items, setItems] = useState<TermsContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingItem, setEditingItem] = useState<TermsContentItem | null>(null);
  const [formData, setFormData] = useState<FormData>({
    item_id: "",
    item_type: "paragraph",
    body: "",
    label: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [draggedItem, setDraggedItem] = useState<TermsContentItem | null>(null);

  // Privacy Policy state
  const [privacyAudience, setPrivacyAudience] = useState<PrivacyPolicyAudience>(null);
  const [privacyContent, setPrivacyContent] = useState<string>("");
  const [privacyLoading, setPrivacyLoading] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const [editorMode, setEditorMode] = useState<'markdown' | 'html'>('markdown');

  useEffect(() => {
    if (activeTab === "terms") {
      loadTermsContent();
    } else {
      loadPrivacyPolicy();
    }
  }, [audience, activeTab, privacyAudience]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  async function loadTermsContent() {
    try {
      setLoading(true);
      const data = await fetchTermsContent(audience);
      setItems(data);
    } catch (err) {
      console.error("Failed to load terms content:", err);
      setError("Failed to load terms content");
    } finally {
      setLoading(false);
    }
  }

  async function loadPrivacyPolicy() {
    try {
      setPrivacyLoading(true);
      const data = await fetchPrivacyPolicy(privacyAudience);
      setPrivacyContent(data?.content || "");
    } catch (err) {
      console.error("Failed to load privacy policy:", err);
      setError("Failed to load privacy policy");
    } finally {
      setPrivacyLoading(false);
    }
  }

  async function handleSavePrivacyPolicy() {
    try {
      setSaving(true);
      setError(null);
      await upsertPrivacyPolicy(privacyAudience, privacyContent);
      const audienceLabel = privacyAudience === 'seeker' ? 'Seeker' : privacyAudience === 'guide' ? 'Guide' : 'Shared';
      setSuccess(`${audienceLabel} privacy policy saved successfully`);
    } catch (err: any) {
      console.error("Error saving privacy policy:", err);
      setError(err.message || "Failed to save privacy policy");
    } finally {
      setSaving(false);
    }
  }

  function openCreateModal() {
    setEditingItem(null);
    setFormData({
      item_id: "",
      item_type: "paragraph",
      body: "",
      label: "",
    });
    setShowCreateModal(true);
    setError(null);
  }

  function openEditModal(item: TermsContentItem) {
    setEditingItem(item);
    setFormData({
      item_id: item.item_id,
      item_type: item.item_type,
      body: item.body,
      label: item.label || "",
    });
    setShowCreateModal(true);
    setError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSaving(true);

    try {
      if (editingItem) {
        // Update existing item
        await updateTermsContentItem(editingItem.id, {
          item_id: formData.item_id,
          item_type: formData.item_type,
          body: formData.body,
          label: formData.item_type === "important" ? formData.label : null,
        });
        setSuccess("Item updated successfully");
      } else {
        // Create new item
        const nextSortOrder = items.length > 0
          ? Math.max(...items.map(i => i.sort_order)) + 1
          : 0;

        await createTermsContentItem({
          audience,
          item_id: formData.item_id,
          item_type: formData.item_type,
          sort_order: nextSortOrder,
          body: formData.body,
          label: formData.item_type === "important" ? formData.label : null,
        });
        setSuccess("Item created successfully");
      }

      setShowCreateModal(false);
      loadTermsContent();
    } catch (err: any) {
      console.error("Error saving item:", err);
      setError(err.message || "Failed to save item");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(item: TermsContentItem) {
    if (!confirm(`Are you sure you want to delete "${item.item_id}"?`)) {
      return;
    }

    try {
      await deleteTermsContentItem(item.id);
      setSuccess("Item deleted successfully");
      loadTermsContent();
    } catch (err) {
      console.error("Error deleting item:", err);
      setError("Failed to delete item");
    }
  }

  // Drag and drop handlers
  function handleDragStart(item: TermsContentItem) {
    setDraggedItem(item);
  }

  function handleDragOver(e: React.DragEvent, targetItem: TermsContentItem) {
    e.preventDefault();

    if (!draggedItem || draggedItem.id === targetItem.id) {
      return;
    }

    const draggedIndex = items.findIndex(i => i.id === draggedItem.id);
    const targetIndex = items.findIndex(i => i.id === targetItem.id);

    if (draggedIndex === targetIndex) return;

    // Reorder items array
    const newItems = [...items];
    newItems.splice(draggedIndex, 1);
    newItems.splice(targetIndex, 0, draggedItem);

    // Update sort_order for all items
    const updatedItems = newItems.map((item, index) => ({
      ...item,
      sort_order: index,
    }));

    setItems(updatedItems);
  }

  async function handleDragEnd() {
    if (!draggedItem) return;

    try {
      // Save new order to database
      const reorderData = items.map((item, index) => ({
        id: item.id,
        sort_order: index,
      }));

      await reorderTermsContent(audience, reorderData);
      setSuccess("Order updated successfully");
    } catch (err) {
      console.error("Error updating order:", err);
      setError("Failed to update order");
      loadTermsContent(); // Reload to get correct order
    } finally {
      setDraggedItem(null);
    }
  }

  // Enhanced markdown renderer for preview with tables, blockquotes, links, etc.
  const renderMarkdownPreview = (markdown: string) => {
    const lines = markdown.split('\n');
    const elements: React.ReactNode[] = [];
    let currentList: string[] = [];
    let listType: 'ul' | 'ol' | null = null;
    let currentBlockquote: string[] = [];
    let currentTable: string[][] = [];
    let tableAlignment: ('left' | 'center' | 'right')[] = [];

    const flushList = () => {
      if (currentList.length > 0) {
        const ListTag = listType === 'ol' ? 'ol' : 'ul';
        elements.push(
          <ListTag key={elements.length} className={listType === 'ol' ? 'list-decimal list-inside space-y-1 mb-4' : 'list-disc list-inside space-y-1 mb-4'}>
            {currentList.map((item, i) => (
              <li key={i} className="text-sm leading-6 text-[#2C2731]">{renderInlineMarkdown(item)}</li>
            ))}
          </ListTag>
        );
        currentList = [];
        listType = null;
      }
    };

    const flushBlockquote = () => {
      if (currentBlockquote.length > 0) {
        elements.push(
          <blockquote key={elements.length} className="border-l-4 border-[#214D3C] pl-4 py-2 my-4 bg-[#214D3C]/5 rounded-r">
            {currentBlockquote.map((line, i) => (
              <p key={i} className="text-sm leading-6 text-[#2C2731] italic">{renderInlineMarkdown(line)}</p>
            ))}
          </blockquote>
        );
        currentBlockquote = [];
      }
    };

    const flushTable = () => {
      if (currentTable.length > 0) {
        const headerRow = currentTable[0];
        const bodyRows = currentTable.slice(1);
        elements.push(
          <div key={elements.length} className="overflow-x-auto my-4">
            <table className="min-w-full border-collapse border border-[#2C2731]/30 text-sm">
              <thead>
                <tr className="bg-[#F2EAC3]">
                  {headerRow.map((cell, i) => (
                    <th
                      key={i}
                      className={`border border-[#2C2731]/30 px-3 py-2 font-semibold text-[#2C2731] ${
                        tableAlignment[i] === 'center' ? 'text-center' :
                        tableAlignment[i] === 'right' ? 'text-right' : 'text-left'
                      }`}
                    >
                      {renderInlineMarkdown(cell.trim())}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bodyRows.map((row, rowIndex) => (
                  <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-[#FFFCEF]'}>
                    {row.map((cell, cellIndex) => (
                      <td
                        key={cellIndex}
                        className={`border border-[#2C2731]/30 px-3 py-2 text-[#2C2731] ${
                          tableAlignment[cellIndex] === 'center' ? 'text-center' :
                          tableAlignment[cellIndex] === 'right' ? 'text-right' : 'text-left'
                        }`}
                      >
                        {renderInlineMarkdown(cell.trim())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        currentTable = [];
        tableAlignment = [];
      }
    };

    const renderInlineMarkdown = (text: string): React.ReactNode => {
      // Process inline elements: links, bold, italic, code, strikethrough
      const elements: React.ReactNode[] = [];
      let remaining = text;
      let keyCounter = 0;

      while (remaining.length > 0) {
        // Check for links [text](url)
        const linkMatch = remaining.match(/^\[([^\]]+)\]\(([^)]+)\)/);
        if (linkMatch) {
          elements.push(
            <a key={keyCounter++} href={linkMatch[2]} className="text-[#214D3C] underline hover:text-[#304031]" target="_blank" rel="noopener noreferrer">
              {linkMatch[1]}
            </a>
          );
          remaining = remaining.slice(linkMatch[0].length);
          continue;
        }

        // Check for inline code `code`
        const codeMatch = remaining.match(/^`([^`]+)`/);
        if (codeMatch) {
          elements.push(
            <code key={keyCounter++} className="bg-[#2C2731]/10 px-1.5 py-0.5 rounded text-sm font-mono">
              {codeMatch[1]}
            </code>
          );
          remaining = remaining.slice(codeMatch[0].length);
          continue;
        }

        // Check for strikethrough ~~text~~
        const strikeMatch = remaining.match(/^~~([^~]+)~~/);
        if (strikeMatch) {
          elements.push(<del key={keyCounter++}>{strikeMatch[1]}</del>);
          remaining = remaining.slice(strikeMatch[0].length);
          continue;
        }

        // Check for bold **text**
        const boldMatch = remaining.match(/^\*\*([^*]+)\*\*/);
        if (boldMatch) {
          elements.push(<strong key={keyCounter++}>{boldMatch[1]}</strong>);
          remaining = remaining.slice(boldMatch[0].length);
          continue;
        }

        // Check for italic *text*
        const italicMatch = remaining.match(/^\*([^*]+)\*/);
        if (italicMatch) {
          elements.push(<em key={keyCounter++}>{italicMatch[1]}</em>);
          remaining = remaining.slice(italicMatch[0].length);
          continue;
        }

        // No match, take next character
        const nextSpecial = remaining.search(/[\[`~*]/);
        if (nextSpecial === -1) {
          elements.push(remaining);
          break;
        } else if (nextSpecial === 0) {
          elements.push(remaining[0]);
          remaining = remaining.slice(1);
        } else {
          elements.push(remaining.slice(0, nextSpecial));
          remaining = remaining.slice(nextSpecial);
        }
      }

      return elements.length === 1 ? elements[0] : elements;
    };

    const parseTableRow = (line: string): string[] => {
      // Remove leading/trailing pipes and split by pipe
      return line.replace(/^\||\|$/g, '').split('|');
    };

    const isTableSeparator = (line: string): boolean => {
      return /^\|?[\s\-:|]+\|?$/.test(line) && line.includes('-');
    };

    const parseTableAlignment = (line: string): ('left' | 'center' | 'right')[] => {
      return parseTableRow(line).map(cell => {
        const trimmed = cell.trim();
        if (trimmed.startsWith(':') && trimmed.endsWith(':')) return 'center';
        if (trimmed.endsWith(':')) return 'right';
        return 'left';
      });
    };

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();

      // Check for table rows
      if (trimmedLine.startsWith('|') || (trimmedLine.includes('|') && currentTable.length > 0)) {
        flushList();
        flushBlockquote();

        if (isTableSeparator(trimmedLine)) {
          // This is the alignment row
          tableAlignment = parseTableAlignment(trimmedLine);
        } else {
          currentTable.push(parseTableRow(trimmedLine));
        }
        return;
      } else if (currentTable.length > 0) {
        flushTable();
      }

      // Check for horizontal rule
      if (/^(-{3,}|\*{3,}|_{3,})$/.test(trimmedLine)) {
        flushList();
        flushBlockquote();
        elements.push(<hr key={index} className="my-6 border-t border-[#2C2731]/30" />);
        return;
      }

      // Check for blockquote
      if (trimmedLine.startsWith('> ')) {
        flushList();
        currentBlockquote.push(trimmedLine.slice(2));
        return;
      } else if (currentBlockquote.length > 0) {
        flushBlockquote();
      }

      // Check for headings
      if (trimmedLine.startsWith('### ')) {
        flushList();
        elements.push(
          <h3 key={index} className="text-base font-semibold text-[#304031] mt-4 mb-2">
            {renderInlineMarkdown(trimmedLine.slice(4))}
          </h3>
        );
      } else if (trimmedLine.startsWith('## ')) {
        flushList();
        elements.push(
          <h2 key={index} className="text-lg font-semibold text-[#304031] mt-6 mb-3">
            {renderInlineMarkdown(trimmedLine.slice(3))}
          </h2>
        );
      } else if (trimmedLine.startsWith('# ')) {
        flushList();
        elements.push(
          <h1 key={index} className="font-recoleta text-xl font-medium text-[#304031] mt-4 mb-3">
            {renderInlineMarkdown(trimmedLine.slice(2))}
          </h1>
        );
      } else if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ')) {
        if (listType !== 'ul') {
          flushList();
          listType = 'ul';
        }
        currentList.push(trimmedLine.slice(2));
      } else if (/^\d+\.\s/.test(trimmedLine)) {
        if (listType !== 'ol') {
          flushList();
          listType = 'ol';
        }
        currentList.push(trimmedLine.replace(/^\d+\.\s/, ''));
      } else if (trimmedLine === '') {
        flushList();
      } else {
        flushList();
        elements.push(
          <p key={index} className="text-sm leading-6 text-[#2C2731] mb-3">
            {renderInlineMarkdown(trimmedLine)}
          </p>
        );
      }
    });

    flushList();
    flushBlockquote();
    flushTable();
    return elements;
  };

  // Convert HTML to Markdown for rich text paste
  const htmlToMarkdown = (html: string): string => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    const processNode = (node: Node): string => {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent || '';
      }

      if (node.nodeType !== Node.ELEMENT_NODE) {
        return '';
      }

      const element = node as HTMLElement;
      const tagName = element.tagName.toLowerCase();
      const children = Array.from(element.childNodes).map(processNode).join('');

      switch (tagName) {
        case 'h1':
          return `# ${children}\n\n`;
        case 'h2':
          return `## ${children}\n\n`;
        case 'h3':
          return `### ${children}\n\n`;
        case 'h4':
          return `#### ${children}\n\n`;
        case 'h5':
          return `##### ${children}\n\n`;
        case 'h6':
          return `###### ${children}\n\n`;
        case 'p':
          return `${children}\n\n`;
        case 'br':
          return '\n';
        case 'strong':
        case 'b':
          return `**${children}**`;
        case 'em':
        case 'i':
          return `*${children}*`;
        case 'del':
        case 's':
        case 'strike':
          return `~~${children}~~`;
        case 'code':
          return `\`${children}\``;
        case 'a':
          const href = element.getAttribute('href') || '';
          return `[${children}](${href})`;
        case 'ul':
          return Array.from(element.children)
            .map(li => `- ${processNode(li).trim()}`)
            .join('\n') + '\n\n';
        case 'ol':
          return Array.from(element.children)
            .map((li, i) => `${i + 1}. ${processNode(li).trim()}`)
            .join('\n') + '\n\n';
        case 'li':
          return children;
        case 'blockquote':
          return children.split('\n').map(line => `> ${line}`).join('\n') + '\n\n';
        case 'hr':
          return '\n---\n\n';
        case 'table':
          return processTable(element);
        case 'pre':
          const codeEl = element.querySelector('code');
          const codeContent = codeEl ? codeEl.textContent : children;
          return `\`\`\`\n${codeContent}\n\`\`\`\n\n`;
        case 'div':
        case 'span':
          return children;
        default:
          return children;
      }
    };

    const processTable = (table: HTMLElement): string => {
      const rows = Array.from(table.querySelectorAll('tr'));
      if (rows.length === 0) return '';

      const processRow = (row: Element): string[] => {
        return Array.from(row.querySelectorAll('th, td')).map(cell =>
          processNode(cell).trim().replace(/\n/g, ' ')
        );
      };

      const headerRow = processRow(rows[0]);
      const separator = headerRow.map(() => '---');
      const bodyRows = rows.slice(1).map(processRow);

      let result = `| ${headerRow.join(' | ')} |\n`;
      result += `| ${separator.join(' | ')} |\n`;
      bodyRows.forEach(row => {
        result += `| ${row.join(' | ')} |\n`;
      });
      result += '\n';

      return result;
    };

    return processNode(tempDiv).trim();
  };

  // Clean up Word HTML - removes MSO styles, unnecessary tags, and normalizes the HTML
  const cleanWordHtml = (html: string): string => {
    // Create a temporary div to parse the HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Remove all style tags
    tempDiv.querySelectorAll('style').forEach(el => el.remove());

    // Remove all script tags
    tempDiv.querySelectorAll('script').forEach(el => el.remove());

    // Remove all meta tags
    tempDiv.querySelectorAll('meta').forEach(el => el.remove());

    // Remove all link tags
    tempDiv.querySelectorAll('link').forEach(el => el.remove());

    // Remove all comments
    const removeComments = (node: Node) => {
      const childNodes = Array.from(node.childNodes);
      childNodes.forEach(child => {
        if (child.nodeType === Node.COMMENT_NODE) {
          child.remove();
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          removeComments(child);
        }
      });
    };
    removeComments(tempDiv);

    // Remove Word-specific elements
    tempDiv.querySelectorAll('[class*="Mso"], [class*="mso"]').forEach(el => {
      el.removeAttribute('class');
    });

    // Remove all inline styles (Word adds tons of these)
    tempDiv.querySelectorAll('[style]').forEach(el => {
      el.removeAttribute('style');
    });

    // Remove empty spans
    tempDiv.querySelectorAll('span').forEach(span => {
      if (!span.hasChildNodes() || (span.textContent?.trim() === '' && !span.querySelector('img'))) {
        span.remove();
      } else if (span.childNodes.length === 1 && span.firstChild?.nodeType === Node.TEXT_NODE) {
        // Replace span with just its text content
        span.replaceWith(span.textContent || '');
      }
    });

    // Convert Word-specific tags to standard HTML
    tempDiv.querySelectorAll('o\\:p, o\\:smarttagtype').forEach(el => el.remove());

    // Clean up tables
    tempDiv.querySelectorAll('table').forEach(table => {
      table.removeAttribute('cellspacing');
      table.removeAttribute('cellpadding');
      table.removeAttribute('border');
      table.removeAttribute('width');
      table.classList.add('border-collapse', 'border', 'border-gray-300', 'w-full', 'my-4');
    });

    tempDiv.querySelectorAll('td, th').forEach(cell => {
      cell.removeAttribute('width');
      cell.removeAttribute('valign');
      cell.classList.add('border', 'border-gray-300', 'px-3', 'py-2');
    });

    // Clean up paragraphs - remove empty ones
    tempDiv.querySelectorAll('p').forEach(p => {
      if (p.textContent?.trim() === '' && !p.querySelector('img, table')) {
        // Check if it's just &nbsp;
        if (p.innerHTML.trim() === '&nbsp;' || p.innerHTML.trim() === '') {
          p.remove();
        }
      }
    });

    // Convert b to strong, i to em for semantic HTML
    tempDiv.querySelectorAll('b').forEach(b => {
      const strong = document.createElement('strong');
      strong.innerHTML = b.innerHTML;
      b.replaceWith(strong);
    });

    tempDiv.querySelectorAll('i').forEach(i => {
      const em = document.createElement('em');
      em.innerHTML = i.innerHTML;
      i.replaceWith(em);
    });

    // Get the cleaned HTML
    let cleanedHtml = tempDiv.innerHTML;

    // Remove excessive whitespace and newlines
    cleanedHtml = cleanedHtml
      .replace(/\r\n/g, '\n')
      .replace(/\n\s*\n\s*\n/g, '\n\n')
      .replace(/>\s+</g, '>\n<')
      .trim();

    return cleanedHtml;
  };

  // Sanitize HTML for safe rendering (basic XSS prevention)
  const sanitizeHtml = (html: string): string => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Remove dangerous tags
    const dangerousTags = ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button'];
    dangerousTags.forEach(tag => {
      tempDiv.querySelectorAll(tag).forEach(el => el.remove());
    });

    // Remove event handlers
    tempDiv.querySelectorAll('*').forEach(el => {
      Array.from(el.attributes).forEach(attr => {
        if (attr.name.startsWith('on')) {
          el.removeAttribute(attr.name);
        }
      });
    });

    // Remove javascript: URLs
    tempDiv.querySelectorAll('a[href^="javascript:"]').forEach(el => {
      el.removeAttribute('href');
    });

    return tempDiv.innerHTML;
  };

  // Handle paste event for rich text conversion
  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const clipboardData = e.clipboardData;
    const html = clipboardData.getData('text/html');

    // If there's HTML content
    if (html && html.includes('<')) {
      e.preventDefault();

      let processedContent: string;

      if (editorMode === 'html') {
        // In HTML mode, clean up Word HTML and insert as HTML
        processedContent = cleanWordHtml(html);
      } else {
        // In Markdown mode, convert to markdown
        processedContent = htmlToMarkdown(html);
      }

      const textarea = e.target as HTMLTextAreaElement;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;

      const newContent = privacyContent.substring(0, start) + processedContent + privacyContent.substring(end);
      setPrivacyContent(newContent);

      // Set cursor position after pasted content
      setTimeout(() => {
        textarea.focus();
        const newPos = start + processedContent.length;
        textarea.setSelectionRange(newPos, newPos);
      }, 0);
    }
    // Otherwise, let the default paste behavior handle plain text
  };

  // Markdown toolbar insert helper
  const insertMarkdown = (prefix: string, suffix: string = "") => {
    const textarea = document.getElementById("privacy-editor") as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = privacyContent.substring(start, end);
    const newText = privacyContent.substring(0, start) + prefix + selectedText + suffix + privacyContent.substring(end);

    setPrivacyContent(newText);

    // Restore cursor position
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + prefix.length + selectedText.length + suffix.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  return (
    <div className="min-h-full bg-[#FFFCEF] text-[#2C2731] p-6 space-y-6">
      {/* Header */}
      <header className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-medium text-[#2C2731] font-recoleta">
            Content Builder
          </h1>
          <p className="text-sm text-[#2C2731]/80 md:text-base">
            Manage Terms of Service and Privacy Policy content
          </p>
        </div>

        {activeTab === "terms" && (
          <button
            type="button"
            onClick={openCreateModal}
            className="inline-flex items-center gap-3 rounded-lg border border-[#2C2731] bg-[#2C2731] px-4 py-2.5 text-sm font-medium text-[#FEF6D6] shadow-md transition-colors hover:bg-[#3C3450]"
          >
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#FFFCEF]/20 text-lg font-semibold">
              +
            </span>
            Add Item
          </button>
        )}
      </header>

      {/* Main Tab Selector (Terms vs Privacy) */}
      <div className="flex gap-3 border-b border-[#2C2731]/20 pb-3">
        <button
          onClick={() => setActiveTab("terms")}
          className={[
            "rounded-lg px-5 py-2.5 text-sm font-medium transition-colors",
            activeTab === "terms"
              ? "bg-[#2C2731] text-[#FEF6D6] shadow-md"
              : "bg-[#FFFCEF] text-[#2C2731] border border-[#2C2731] hover:bg-[#F2EAC3]",
          ].join(" ")}
        >
          Terms of Service
        </button>
        <button
          onClick={() => setActiveTab("privacy")}
          className={[
            "rounded-lg px-5 py-2.5 text-sm font-medium transition-colors",
            activeTab === "privacy"
              ? "bg-[#2C2731] text-[#FEF6D6] shadow-md"
              : "bg-[#FFFCEF] text-[#2C2731] border border-[#2C2731] hover:bg-[#F2EAC3]",
          ].join(" ")}
        >
          Privacy Policy
        </button>
      </div>

      {/* Success Alert */}
      {success && (
        <div className="rounded-2xl border border-[#214D3C]/40 bg-[#E4F1E8] text-[#214D3C] px-4 py-3 text-sm flex items-start justify-between gap-4">
          <p>{success}</p>
          <button
            type="button"
            onClick={() => setSuccess(null)}
            className="text-xs font-semibold uppercase tracking-wide"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="rounded-2xl border border-[#B24E4E]/40 bg-[#FCEDEA] text-[#8C2F2F] px-4 py-3 text-sm flex items-start justify-between gap-4">
          <p>{error}</p>
          <button
            type="button"
            onClick={() => setError(null)}
            className="text-xs font-semibold uppercase tracking-wide"
          >
            Dismiss
          </button>
        </div>
      )}

      {activeTab === "terms" && (
        <>
          {/* Audience Selector */}
          <div className="flex gap-3">
            <button
              onClick={() => setAudience("seeker")}
              className={[
                "rounded-lg px-5 py-2.5 text-sm font-medium transition-colors",
                audience === "seeker"
                  ? "bg-[#214D3C] text-[#FEF6D6] shadow-md"
                  : "bg-[#FFFCEF] text-[#2C2731] border border-[#2C2731] hover:bg-[#F2EAC3]",
              ].join(" ")}
            >
              Seeker Terms
            </button>
            <button
              onClick={() => setAudience("guide")}
              className={[
                "rounded-lg px-5 py-2.5 text-sm font-medium transition-colors",
                audience === "guide"
                  ? "bg-[#214D3C] text-[#FEF6D6] shadow-md"
                  : "bg-[#FFFCEF] text-[#2C2731] border border-[#2C2731] hover:bg-[#F2EAC3]",
              ].join(" ")}
            >
              Guide Terms
            </button>
          </div>

          {/* Items List */}
          <div className="space-y-3">
            {loading && (
              <div className="text-center py-8 text-sm text-[#2C2731]/70">
                Loading...
              </div>
            )}

            {!loading && items.length === 0 && (
              <div className="text-center py-8 text-sm text-[#2C2731]/70">
                No items found. Click "Add Item" to create your first term.
              </div>
            )}

            {!loading &&
              items.map((item) => (
                <div
                  key={item.id}
                  draggable
                  onDragStart={() => handleDragStart(item)}
                  onDragOver={(e) => handleDragOver(e, item)}
                  onDragEnd={handleDragEnd}
                  className={[
                    "rounded-2xl border border-[#2C2731] bg-[#FFFCEF] p-4 shadow-sm cursor-move hover:shadow-md transition-shadow",
                    draggedItem?.id === item.id ? "opacity-50" : "",
                  ].join(" ")}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-xs font-semibold uppercase tracking-wide text-[#2C2731]/70">
                          #{item.sort_order}
                        </span>
                        <span
                          className={[
                            "rounded-full px-2 py-0.5 text-xs font-medium",
                            item.item_type === "important"
                              ? "bg-[#B24E4E]/20 text-[#8C2F2F]"
                              : "bg-[#2C2731]/10 text-[#2C2731]",
                          ].join(" ")}
                        >
                          {item.item_type}
                        </span>
                        <span className="text-sm font-medium text-[#2C2731]">
                          {item.item_id}
                        </span>
                      </div>

                      {item.item_type === "important" && item.label && (
                        <p className="text-sm font-semibold text-[#2C2731] mb-1">
                          {item.label}
                        </p>
                      )}

                      <p className="text-sm text-[#2C2731]/80 whitespace-pre-wrap">
                        {item.body}
                      </p>
                    </div>

                    <div className="flex items-start gap-2">
                      <button
                        onClick={() => openEditModal(item)}
                        className="rounded-md border border-[#2C2731] bg-[#FFFCEF] px-3 py-1.5 text-xs font-medium text-[#2C2731] transition-colors hover:bg-[#F2EAC3]"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(item)}
                        className="rounded-md border border-[#B24E4E] bg-[#FFFCEF] px-3 py-1.5 text-xs font-medium text-[#8C2F2F] transition-colors hover:bg-[#FCEDEA]"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
          </div>

          {/* Summary */}
          {!loading && (
            <div className="text-sm text-[#2C2731]/80">
              Total items: {items.length}
            </div>
          )}
        </>
      )}

      {activeTab === "privacy" && (
        <div className="space-y-4">
          {/* Privacy Policy Audience Selector */}
          <div className="flex gap-3">
            <button
              onClick={() => setPrivacyAudience(null)}
              className={[
                "rounded-lg px-5 py-2.5 text-sm font-medium transition-colors",
                privacyAudience === null
                  ? "bg-[#214D3C] text-[#FEF6D6] shadow-md"
                  : "bg-[#FFFCEF] text-[#2C2731] border border-[#2C2731] hover:bg-[#F2EAC3]",
              ].join(" ")}
            >
              Shared (All)
            </button>
            <button
              onClick={() => setPrivacyAudience("seeker")}
              className={[
                "rounded-lg px-5 py-2.5 text-sm font-medium transition-colors",
                privacyAudience === "seeker"
                  ? "bg-[#214D3C] text-[#FEF6D6] shadow-md"
                  : "bg-[#FFFCEF] text-[#2C2731] border border-[#2C2731] hover:bg-[#F2EAC3]",
              ].join(" ")}
            >
              Seeker Only
            </button>
            <button
              onClick={() => setPrivacyAudience("guide")}
              className={[
                "rounded-lg px-5 py-2.5 text-sm font-medium transition-colors",
                privacyAudience === "guide"
                  ? "bg-[#214D3C] text-[#FEF6D6] shadow-md"
                  : "bg-[#FFFCEF] text-[#2C2731] border border-[#2C2731] hover:bg-[#F2EAC3]",
              ].join(" ")}
            >
              Guide Only
            </button>
          </div>

          <p className="text-xs text-[#2C2731]/60">
            {privacyAudience === null
              ? "Shared privacy policy is shown when no audience-specific policy exists."
              : `This privacy policy will only be shown to ${privacyAudience}s. If empty, they will see the shared policy.`}
          </p>

          <div className="flex items-center justify-between flex-wrap gap-3">
            <p className="text-sm text-[#2C2731]/80">
              {editorMode === 'markdown'
                ? "Edit using Markdown formatting. Paste from Word/Docs to auto-convert."
                : "HTML mode - paste Word documents directly. HTML will be cleaned automatically."}
            </p>
            <div className="flex items-center gap-3">
              {/* Mode Toggle */}
              <div className="flex rounded-lg border border-[#2C2731] overflow-hidden">
                <button
                  type="button"
                  onClick={() => setEditorMode('markdown')}
                  className={[
                    "px-3 py-1.5 text-xs font-medium transition-colors",
                    editorMode === 'markdown'
                      ? "bg-[#2C2731] text-[#FEF6D6]"
                      : "bg-[#FFFCEF] text-[#2C2731] hover:bg-[#F2EAC3]",
                  ].join(" ")}
                >
                  Markdown
                </button>
                <button
                  type="button"
                  onClick={() => setEditorMode('html')}
                  className={[
                    "px-3 py-1.5 text-xs font-medium transition-colors border-l border-[#2C2731]",
                    editorMode === 'html'
                      ? "bg-[#2C2731] text-[#FEF6D6]"
                      : "bg-[#FFFCEF] text-[#2C2731] hover:bg-[#F2EAC3]",
                  ].join(" ")}
                >
                  HTML
                </button>
              </div>
              <button
                type="button"
                onClick={() => setShowPreview(!showPreview)}
                className={[
                  "rounded-lg px-4 py-2 text-sm font-medium transition-colors",
                  showPreview
                    ? "bg-[#214D3C] text-[#FEF6D6]"
                    : "border border-[#2C2731] text-[#2C2731] hover:bg-[#F2EAC3]",
                ].join(" ")}
              >
                {showPreview ? "Hide Preview" : "Show Preview"}
              </button>
              <button
                type="button"
                onClick={handleSavePrivacyPolicy}
                disabled={saving || privacyLoading}
                className="rounded-lg border border-[#2C2731] bg-[#2C2731] px-4 py-2 text-sm font-medium text-[#FEF6D6] transition-colors hover:bg-[#3C3450] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? "Saving..." : "Save Privacy Policy"}
              </button>
            </div>
          </div>

          {/* Mode-specific toolbar/info */}
          {editorMode === 'markdown' ? (
            <>
              {/* Markdown Toolbar */}
              <div className="flex flex-wrap gap-2 p-3 bg-[#F2EAC3] rounded-lg border border-[#2C2731]/20">
                <button
                  type="button"
                  onClick={() => insertMarkdown("# ", "")}
                  className="rounded px-2 py-1 text-xs font-medium text-[#2C2731] hover:bg-[#FFFCEF] transition-colors"
                  title="Heading 1"
                >
                  H1
                </button>
                <button
                  type="button"
                  onClick={() => insertMarkdown("## ", "")}
                  className="rounded px-2 py-1 text-xs font-medium text-[#2C2731] hover:bg-[#FFFCEF] transition-colors"
                  title="Heading 2"
                >
                  H2
                </button>
                <button
                  type="button"
                  onClick={() => insertMarkdown("### ", "")}
                  className="rounded px-2 py-1 text-xs font-medium text-[#2C2731] hover:bg-[#FFFCEF] transition-colors"
                  title="Heading 3"
                >
                  H3
                </button>
                <span className="w-px bg-[#2C2731]/30" />
                <button
                  type="button"
                  onClick={() => insertMarkdown("**", "**")}
                  className="rounded px-2 py-1 text-xs font-bold text-[#2C2731] hover:bg-[#FFFCEF] transition-colors"
                  title="Bold"
                >
                  B
                </button>
                <button
                  type="button"
                  onClick={() => insertMarkdown("*", "*")}
                  className="rounded px-2 py-1 text-xs italic text-[#2C2731] hover:bg-[#FFFCEF] transition-colors"
                  title="Italic"
                >
                  I
                </button>
                <button
                  type="button"
                  onClick={() => insertMarkdown("~~", "~~")}
                  className="rounded px-2 py-1 text-xs line-through text-[#2C2731] hover:bg-[#FFFCEF] transition-colors"
                  title="Strikethrough"
                >
                  S
                </button>
                <button
                  type="button"
                  onClick={() => insertMarkdown("`", "`")}
                  className="rounded px-2 py-1 text-xs font-mono text-[#2C2731] hover:bg-[#FFFCEF] transition-colors"
                  title="Inline Code"
                >
                  {"</>"}
                </button>
                <span className="w-px bg-[#2C2731]/30" />
                <button
                  type="button"
                  onClick={() => insertMarkdown("- ", "")}
                  className="rounded px-2 py-1 text-xs font-medium text-[#2C2731] hover:bg-[#FFFCEF] transition-colors"
                  title="Bullet List"
                >
                  List
                </button>
                <button
                  type="button"
                  onClick={() => insertMarkdown("1. ", "")}
                  className="rounded px-2 py-1 text-xs font-medium text-[#2C2731] hover:bg-[#FFFCEF] transition-colors"
                  title="Numbered List"
                >
                  1. 2. 3.
                </button>
                <span className="w-px bg-[#2C2731]/30" />
                <button
                  type="button"
                  onClick={() => {
                    const linkText = prompt("Link text:", "Link");
                    if (linkText) {
                      const url = prompt("URL:", "https://");
                      if (url) {
                        insertMarkdown(`[${linkText}](${url})`, "");
                      }
                    }
                  }}
                  className="rounded px-2 py-1 text-xs font-medium text-[#2C2731] hover:bg-[#FFFCEF] transition-colors"
                  title="Insert Link"
                >
                  Link
                </button>
                <button
                  type="button"
                  onClick={() => insertMarkdown("> ", "")}
                  className="rounded px-2 py-1 text-xs font-medium text-[#2C2731] hover:bg-[#FFFCEF] transition-colors"
                  title="Blockquote"
                >
                  Quote
                </button>
                <button
                  type="button"
                  onClick={() => insertMarkdown("\n---\n", "")}
                  className="rounded px-2 py-1 text-xs font-medium text-[#2C2731] hover:bg-[#FFFCEF] transition-colors"
                  title="Horizontal Rule"
                >
                  ---
                </button>
                <span className="w-px bg-[#2C2731]/30" />
                <button
                  type="button"
                  onClick={() => {
                    const cols = prompt("Number of columns:", "3");
                    const rows = prompt("Number of rows (excluding header):", "2");
                    if (cols && rows) {
                      const numCols = parseInt(cols) || 3;
                      const numRows = parseInt(rows) || 2;
                      const header = "| " + Array(numCols).fill("Header").join(" | ") + " |";
                      const separator = "| " + Array(numCols).fill("---").join(" | ") + " |";
                      const bodyRows = Array(numRows)
                        .fill(null)
                        .map(() => "| " + Array(numCols).fill("Cell").join(" | ") + " |")
                        .join("\n");
                      insertMarkdown(`\n${header}\n${separator}\n${bodyRows}\n`, "");
                    }
                  }}
                  className="rounded px-2 py-1 text-xs font-medium text-[#2C2731] hover:bg-[#FFFCEF] transition-colors"
                  title="Insert Table"
                >
                  Table
                </button>
              </div>

              <p className="text-xs text-[#2C2731]/60 italic">
                Tip: Paste rich text from Word, Google Docs, or web pages - it will be converted to Markdown automatically.
              </p>
            </>
          ) : (
            <>
              {/* HTML Mode Info */}
              <div className="p-4 bg-[#214D3C]/10 rounded-lg border border-[#214D3C]/30">
                <h3 className="text-sm font-semibold text-[#214D3C] mb-2">HTML Mode</h3>
                <ul className="text-xs text-[#2C2731]/80 space-y-1 list-disc list-inside">
                  <li>Paste directly from Microsoft Word, Google Docs, or any web page</li>
                  <li>Word's messy HTML will be automatically cleaned up</li>
                  <li>Tables, formatting, and structure will be preserved</li>
                  <li>You can also write or edit HTML directly</li>
                </ul>
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    onClick={() => setPrivacyContent('')}
                    className="rounded px-3 py-1.5 text-xs font-medium text-[#8C2F2F] bg-[#FCEDEA] border border-[#B24E4E]/30 hover:bg-[#B24E4E]/20 transition-colors"
                  >
                    Clear All
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      // Re-clean the current content
                      if (privacyContent) {
                        setPrivacyContent(cleanWordHtml(privacyContent));
                        setSuccess("HTML cleaned up!");
                      }
                    }}
                    className="rounded px-3 py-1.5 text-xs font-medium text-[#214D3C] bg-[#E4F1E8] border border-[#214D3C]/30 hover:bg-[#214D3C]/20 transition-colors"
                  >
                    Re-clean HTML
                  </button>
                </div>
              </div>
            </>
          )}

          {privacyLoading ? (
            <div className="text-center py-8 text-sm text-[#2C2731]/70">
              Loading...
            </div>
          ) : (
            <div className={`grid gap-4 ${showPreview ? "grid-cols-2" : "grid-cols-1"}`}>
              {/* Editor */}
              <div className="space-y-2">
                <label htmlFor="privacy-editor" className="text-sm font-medium text-[#2C2731]/80">
                  {editorMode === 'markdown' ? 'Markdown Editor' : 'HTML Editor'}
                </label>
                <textarea
                  id="privacy-editor"
                  value={privacyContent}
                  onChange={(e) => setPrivacyContent(e.target.value)}
                  onPaste={handlePaste}
                  className="w-full h-[500px] rounded-lg border border-[#2C2731] bg-[#FFFCEF] px-4 py-3 text-sm text-[#2C2731] font-mono focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]/30 resize-none"
                  placeholder={editorMode === 'markdown'
                    ? "# Privacy Policy\n\nWrite your privacy policy content here using Markdown formatting..."
                    : "<h1>Privacy Policy</h1>\n\n<p>Paste your Word document here or write HTML directly...</p>"
                  }
                />
              </div>

              {/* Preview */}
              {showPreview && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-[#2C2731]/80">
                    Preview
                  </label>
                  <div className="w-full h-[500px] rounded-lg border border-[#2C2731]/30 bg-white/70 px-4 py-3 overflow-y-auto privacy-preview">
                    {privacyContent ? (
                      editorMode === 'markdown' ? (
                        renderMarkdownPreview(privacyContent)
                      ) : (
                        <div
                          className="prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ __html: sanitizeHtml(privacyContent) }}
                        />
                      )
                    ) : (
                      <p className="text-sm text-[#2C2731]/50 italic">
                        Preview will appear here...
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Modal for Terms */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-[#2C2731]/70 px-4 py-16 overflow-y-auto">
          <div className="w-full max-w-2xl rounded-2xl border border-[#2C2731] bg-[#FFFCEF] text-[#2C2731] shadow-xl">
            <div className="flex items-center justify-between border-b border-[#2C2731]/20 px-6 py-4">
              <h2 className="text-lg font-medium font-recoleta">
                {editingItem ? "Edit Item" : "Create New Item"}
              </h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setError(null);
                }}
                className="rounded-md border border-[#2C2731] bg-[#FFFCEF] px-2 py-1 text-sm font-medium text-[#2C2731] transition-colors hover:bg-[#F2EAC3]"
                aria-label="Close modal"
              >
                X
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5 px-6 py-5">
              {error && (
                <div className="rounded-lg border border-[#B24E4E]/40 bg-[#FCEDEA] text-[#8C2F2F] px-4 py-3 text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <label htmlFor="item_id" className="text-sm font-medium text-[#2C2731]/80">
                  Item ID *
                </label>
                <input
                  id="item_id"
                  type="text"
                  required
                  value={formData.item_id}
                  onChange={(e) => setFormData({ ...formData, item_id: e.target.value })}
                  className="w-full rounded-lg border border-[#2C2731] bg-[#FFFCEF] px-3 py-2 text-sm text-[#2C2731] focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]/30"
                  placeholder="e.g., intro, usage_rights, contact_info"
                />
                <p className="text-xs text-[#2C2731]/60">
                  Unique identifier for this item (used in code)
                </p>
              </div>

              <div className="space-y-2">
                <label htmlFor="item_type" className="text-sm font-medium text-[#2C2731]/80">
                  Item Type *
                </label>
                <select
                  id="item_type"
                  required
                  value={formData.item_type}
                  onChange={(e) =>
                    setFormData({ ...formData, item_type: e.target.value as TermsItemType })
                  }
                  className="w-full rounded-lg border border-[#2C2731] bg-[#FFFCEF] px-3 py-2 text-sm text-[#2C2731] focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]/30"
                >
                  <option value="paragraph">Paragraph</option>
                  <option value="important">Important Statement</option>
                </select>
              </div>

              {formData.item_type === "important" && (
                <div className="space-y-2">
                  <label htmlFor="label" className="text-sm font-medium text-[#2C2731]/80">
                    Label *
                  </label>
                  <input
                    id="label"
                    type="text"
                    required={formData.item_type === "important"}
                    value={formData.label}
                    onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                    className="w-full rounded-lg border border-[#2C2731] bg-[#FFFCEF] px-3 py-2 text-sm text-[#2C2731] focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]/30"
                    placeholder="e.g., Important Notice, Privacy Reminder"
                  />
                  <p className="text-xs text-[#2C2731]/60">
                    Bold heading for important statements
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <label htmlFor="body" className="text-sm font-medium text-[#2C2731]/80">
                  Body Text *
                </label>
                <textarea
                  id="body"
                  required
                  rows={8}
                  value={formData.body}
                  onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                  className="w-full rounded-lg border border-[#2C2731] bg-[#FFFCEF] px-3 py-2 text-sm text-[#2C2731] focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]/30"
                  placeholder="Enter the content for this term..."
                />
              </div>

              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setError(null);
                  }}
                  className="rounded-lg border border-[#2C2731] px-4 py-2 text-sm font-medium text-[#2C2731] transition-colors hover:bg-[#F2EAC3]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-lg border border-[#2C2731] bg-[#2C2731] px-4 py-2 text-sm font-medium text-[#FEF6D6] transition-colors hover:bg-[#3C3450] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? "Saving..." : editingItem ? "Update Item" : "Create Item"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
